import { IconButton } from '@fullscreendigital/carrefour-dex-fe'
import { useCallback, useEffect, useRef, useState } from 'react'
import Webcam from 'react-webcam'
import ImageIcon from '@mui/icons-material/Image'
import { FlashlightOff, FlashlightOn } from '@mui/icons-material'

const defaultVideoConstraints = {
  width: 1280,
  height: 720,
  facingMode: 'environment'
}

interface VideoConstraints {
  width: number
  height: number
  facingMode: string
}
export interface WebcamCaptureProps {
  onImageCapture: Function
  videoConstraints?: VideoConstraints
  buttonLabel?: string
  buttonStyle?: any
  showTorchToggle?: boolean
  style?: any
}

const WebcamCapture = (props: WebcamCaptureProps) => {
  const { onImageCapture, videoConstraints, buttonLabel, buttonStyle, showTorchToggle, style } =
    props

  const webcamRef = useRef(null)

  const trackRef = useRef<MediaStreamTrack | null>(null)
  const [torchOn, setTorchOn] = useState(false)

  // Request camera with torch capability
  const getCameraWithTorch = async () => {
    try {
      const constraints = {
        video: {
          facingMode: 'environment', // Rear camera
          width: { ideal: videoConstraints?.width || defaultVideoConstraints.width },
          height: { ideal: videoConstraints?.height || defaultVideoConstraints.height }
          // advanced: [{ torch: torchOn }] // Request torch capability
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints as any)
      const videoTrack = stream.getVideoTracks()[0]

      trackRef.current = videoTrack

      // Manually set stream to webcam component
      // if (webcamRef.current) {
      //   webcamRef.current.video.srcObject = stream
      // }

      // Check if torch is available
      const capabilities = videoTrack.getCapabilities() as MediaTrackCapabilities & {
        torch?: boolean
      }
      if (capabilities.torch) {
        console.log('Torch is supported on this device')
      } else {
        console.log('Torch is not supported')
        alert('Torch is not supported')
      }
    } catch (err) {
      console.error('Error accessing camera:', err)
      alert('Error accessing camera: ' + err.message)
    }
  }

  const toggleTorch = async enable => {
    if (!trackRef.current) return

    const capabilities = trackRef.current.getCapabilities() as MediaTrackCapabilities & {
      torch?: boolean
    }

    if ((capabilities as any).torch) {
      try {
        await trackRef.current.applyConstraints({
          advanced: [{ torch: enable }] as any
        })
        setTorchOn(enable)
      } catch (err) {
        console.error('Torch toggle failed:', err)
        alert('Torch toggle failed')
      }
    }
  }

  useEffect(() => {
    getCameraWithTorch()

    return () => {
      console.log('cleaning up track', trackRef.current)
      if (trackRef.current) {
        trackRef.current.stop()
      }

      // Also stop all tracks on the stream if available
      const video = webcamRef.current?.video
      const stream = video?.srcObject as MediaStream

      if (stream) {
        stream.getTracks().forEach(track => track.stop())
      }

      if (video) {
        video.srcObject = null
      }
    }
  }, [])

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot()
    onImageCapture(imageSrc)
  }, [webcamRef])

  return (
    <>
      <Webcam
        audio={false}
        ref={webcamRef}
        screenshotFormat="image/jpeg"
        screenshotQuality={1}
        forceScreenshotSourceSize
        videoConstraints={
          videoConstraints
            ? {
                ...videoConstraints,
                width: { ideal: videoConstraints.width },
                height: { ideal: videoConstraints.height }
              }
            : defaultVideoConstraints
        }
        style={style || { maxWidth: '100%' }}
      />
      <IconButton
        label={buttonLabel || 'Captura foto'}
        onClick={capture}
        startIcon={<ImageIcon />}
        variant="outlined"
        style={buttonStyle}
      />
      {showTorchToggle && (
        <IconButton
          label={buttonLabel || torchOn ? 'Stingeti lanterna' : 'Aprindeti lanterna'}
          onClick={() => toggleTorch(!torchOn)}
          startIcon={torchOn ? <FlashlightOff /> : <FlashlightOn />}
          variant="outlined"
          style={{ ...buttonStyle, marginTop: '12px' }}
        />
      )}
    </>
  )
}

export default WebcamCapture
